import { useCallback } from 'react';

import { IUserOnboardingFlowStateEnum } from '@r-client/data/graphql-types';
import { useReporting } from '@r-client/shared/data/error-reporting';

import { useUpdateOnboardingFlowStateMutation } from '../graphql/update-onboarding-flow-state-mutation';

/**
 * Records where the user is in the onboarding funnel.
 *
 * Always resolves — a failed write is reported and swallowed, never surfaced to
 * the user and never allowed to block navigation or login. There is no toast:
 * unlike the step-status mutation, this state doesn't change what the user sees
 * next in the flow, so there is nothing actionable to tell them.
 *
 * Callers should still `await` this when a `router.push` follows, so the request
 * is in flight before the component unmounts.
 */
export const useUpdateOnboardingFlowState = () => {
  const mutation = useUpdateOnboardingFlowStateMutation();
  const reporting = useReporting();

  return useCallback(
    async (onboardingFlowState: IUserOnboardingFlowStateEnum) => {
      const notifyError = (context: Record<string, unknown>) =>
        reporting.error('UsOnboarding#updateFlowState: ', {
          onboardingFlowState,
          ...context,
        });

      try {
        const result = await mutation.submit({
          variables: { onboardingFlowState },
        });

        // submit() returns false on network/Apollo failure
        if (result === false) {
          notifyError({ reason: 'mutation_submission_failed' });
          return;
        }

        if (result.errors?.length) {
          notifyError({ errors: result.errors });
          return;
        }

        const graphqlErrors =
          result.data?.userOnboardingFlowStateUpdate?.errors;
        if (graphqlErrors?.length) {
          notifyError({ errors: graphqlErrors });
        }
      } catch (error) {
        notifyError({ error });
      }
    },
    [mutation, reporting]
  );
};
